import { memo, useCallback, type ReactNode } from 'react';
import { Pressable, StatusBar, StyleSheet } from 'react-native';

import { Stack } from '@/components/layout';
import { Button, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import {
  EventDetailContent,
  EventDetailHeader,
  EventDetailSkeleton,
  type EventDetailSurface,
} from '@/features/guardian/components/event-detail';
import { useEventDetailActions } from '@/features/guardian/hooks/use-event-detail-actions';
import { useEventDetail } from '@/features/guardian/hooks/use-event-detail';
import { createStyles, useScaling } from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

const strings = STRINGS.guardian.kids.eventDetail;

type GuardianEventDetailScreenProps = GuardianStackScreenProps<'GuardianEventDetail'>;

type EventUnavailableProps = {
  surface: EventDetailSurface;
  onClose: () => void;
};

function EventUnavailable({ surface, onClose }: EventUnavailableProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack flex={1} style={styles.surface}>
      <EventDetailHeader
        title={strings.screenTitle}
        surface={surface}
        showMore={false}
        onClose={onClose}
      />
      <Stack flex={1} align="center" justify="center" gap={scale(16)} paddingX={scale(24)}>
        <Text variant="Font Guardian App/Body/Body 16 Semibold" style={styles.notFoundText}>
          {strings.notFound}
        </Text>
        <Button onPress={onClose} style={styles.closeButton}>
          <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold">
            {strings.close}
          </Button.Text>
        </Button>
      </Stack>
    </Stack>
  );
}

function GuardianEventDetailScreen({ navigation, route }: GuardianEventDetailScreenProps) {
  const { isTablet } = useScaling();
  const styles = useStyles();
  const { event, isLoading } = useEventDetail(route.params.eventId);
  const { handleOpenMap, handleShare, handleReschedule, handleViewBooking } =
    useEventDetailActions();
  const surface: EventDetailSurface = isTablet ? 'popup' : 'screen';
  const handleClose = useCallback(() => navigation.goBack(), [navigation]);

  let content: ReactNode;
  if (isLoading) {
    content = <EventDetailSkeleton surface={surface} />;
  } else if (event) {
    content = (
      <EventDetailContent
        event={event}
        surface={surface}
        onClose={handleClose}
        onOpenMap={handleOpenMap}
        onShare={handleShare}
        onReschedule={handleReschedule}
        onViewBooking={handleViewBooking}
      />
    );
  } else {
    content = <EventUnavailable surface={surface} onClose={handleClose} />;
  }

  return (
    <Stack flex={1} style={isTablet ? styles.overlay : styles.surface}>
      <StatusBar barStyle="dark-content" backgroundColor="transparent" translucent />
      {isTablet ? (
        <>
          <Pressable
            accessibilityLabel={strings.close}
            accessibilityRole="button"
            onPress={handleClose}
            style={styles.backdrop}
          />
          <Stack style={styles.popup}>{content}</Stack>
        </>
      ) : (
        content
      )}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  surface: {
    backgroundColor: guardianColors['1'],
  },
  overlay: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: scale(24),
    backgroundColor: withOpacity(guardianColors['10'], 0.24),
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  popup: {
    width: scale({ phone: 343, tablet: 480 }),
    maxWidth: '100%',
    height: '88%',
    maxHeight: scale(680),
    borderRadius: scale(16),
    borderCurve: 'continuous',
    overflow: 'hidden',
    backgroundColor: colors['0'],
  },
  notFoundText: {
    color: guardianColors['10'],
    textAlign: 'center',
  },
  closeButton: {
    minWidth: scale(160),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    backgroundColor: guardianColors['Main/Main'],
  },
}));

export { GuardianEventDetailScreen };
export default memo(GuardianEventDetailScreen);
